import { useMemo, useState } from 'react';
import cx from 'classnames';
import { lowerCase } from 'lodash';
import PropTypes from 'prop-types';

import { PROMPT_ACTION } from 'src/data/prompt';
import { isValidUrl } from 'src/lib/url';

import { createNewUrlPromptMenuItem } from '../../lib';
import { EmptyMenuState } from '../EmptyMenuState';
import { ItemsUrl } from '../ItemsUrl';
import { MenuSearchField } from '../MenuSearchField';

import { useStyles } from './useStyles';

const propTypes = {
  className: PropTypes.string,
  urlResources: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  onAction: PropTypes.func.isRequired,
  quickSearchMode: PropTypes.bool,
  noTags: PropTypes.bool,
};

const defaultProps = {
  className: '',
  quickSearchMode: false,
  noTags: false,
};

export const MenuContentUrl = ({ className, urlResources, onAction, quickSearchMode, noTags }) => {
  const styles = useStyles();

  const [searchValue, setSearchValue] = useState('');

  const filteredResources = useMemo(() => {
    if (!searchValue) {
      return urlResources;
    }

    const preparedSearchText = lowerCase(searchValue);

    return urlResources.filter(
      (resource) =>
        lowerCase(resource.name).includes(preparedSearchText) ||
        lowerCase(resource.content).includes(preparedSearchText)
    );
  }, [urlResources, searchValue]);

  const additionalItem = useMemo(() => {
    if (filteredResources?.length > 0 || searchValue?.length === 0) {
      return null;
    }

    return createNewUrlPromptMenuItem(searchValue, () =>
      onAction(PROMPT_ACTION.insertUrl, { url: searchValue, noTags })
    );
  }, [filteredResources, searchValue, onAction, noTags]);

  const handleSearchChange = (value) => {
    setSearchValue(value.trim());
  };

  return (
    <div className={cx(styles.root, className)}>
      <MenuSearchField
        data-testid="menuContentUrl"
        className={styles.searchField}
        value={searchValue}
        onChange={handleSearchChange}
        onEnterPress={() => {
          if (isValidUrl(searchValue)) {
            onAction(PROMPT_ACTION.insertUrl, { url: searchValue, noTags });
          }
        }}
        placeholder="Enter URL"
        autoFocus
        hideIcon
      />

      {filteredResources.length > 0 || additionalItem ? (
        <ItemsUrl
          urlResources={filteredResources}
          onAction={onAction}
          quickSearchMode={quickSearchMode}
          noTags={noTags}
          additionalItem={additionalItem}
        />
      ) : (
        <EmptyMenuState
          searchText={searchValue}
          emptyText="No saved resources yet"
        />
      )}
    </div>
  );
};

MenuContentUrl.displayName = 'MenuContentUrl';
MenuContentUrl.propTypes = propTypes;
MenuContentUrl.defaultProps = defaultProps;
